import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import AppHeader from '../components/AppHeader';
import BottomNav from '../components/BottomNav';
import { useAuth } from '../context/AuthContext';
import { handleIOSPayPerUse } from '../utils/iapPayment';
import appleIAP, { PRODUCT_IDS } from '../services/appleIAP';

const REELS_PRODUCT_ID = PRODUCT_IDS.doctor.reels;

const ReelSubscriptionPage = () => {
  const { user } = useAuth();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [caption, setCaption] = useState('');
  const [duration, setDuration] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');
  const [applePrice, setApplePrice] = useState(null);
  const [iapFinished, setIapFinished] = useState(false);
  
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => { 
    fetchSubscription(); 
    initIAP(); 
  }, []);

  const initIAP = async () => {
    try {
      await appleIAP.initialize();
      
      const loadPrice = () => {
        const p = appleIAP.getProduct(REELS_PRODUCT_ID);
        if (p) setApplePrice(p.localizedPrice);
      };
      
      loadPrice();
      const removeListener = appleIAP.on('products-updated', loadPrice);
      
      let attempts = 0;
      const pollInterval = setInterval(() => {
        attempts++;
        const hasPrice = appleIAP.getProduct(REELS_PRODUCT_ID)?.localizedPrice;
        if (hasPrice || attempts >= 10) {
          clearInterval(pollInterval);
          loadPrice();
          setIapFinished(true);
          removeListener?.();
        } else {
          loadPrice();
        }
      }, 1000);

      return () => {
        clearInterval(pollInterval);
        removeListener?.();
      };
    } catch (err) {
      console.error('IAP init error:', err);
      setIapFinished(true);
    }
  };

  const fetchSubscription = async () => {
    try {
      const res = await api.get('/reel-subscriptions/my');
      if (res.data.success) setSub(res.data.data);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  };

  const buySubscription = async () => {
    const userId = user?._id || user?.id || '';
    if (!userId) { setMessage('Please log in first'); setMessageType('error'); return; }
    if (!applePrice) {
      setMessage('Price is currently unavailable. Please try again in a moment.'); 
      setMessageType('error'); 
      return;
    }

    await handleIOSPayPerUse({
      productId: REELS_PRODUCT_ID,
      onSuccess: async () => {
        setMessage('Subscription activated!'); setMessageType('success');
        fetchSubscription();
      },
      onError: (msg) => { setMessage(msg); setMessageType('error'); },
    });
  };

  const handleFileSelect = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    const url = URL.createObjectURL(f);
    setPreview(url);
    setMessage('');
  };

  const uploadReel = async () => {
    if (!file) { setMessage('Please select a video file'); setMessageType('error'); return; }
    const dur = parseInt(duration) || 0;
    if (dur > 30) { setMessage('Duration must not exceed 30 seconds'); setMessageType('error'); return; }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('reel_file', file);
      formData.append('caption', caption);
      formData.append('duration', dur);

      const res = await api.post('/reels/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.success) {
        setMessage('Reel uploaded for review!'); setMessageType('success');
        setFile(null); setPreview(null); setCaption(''); setDuration('');
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    } catch (e) {
      setMessage(e.response?.data?.message || 'Upload failed'); setMessageType('error');
    }
    finally { setUploading(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-dark flex flex-col">
      <AppHeader />
      <div className="flex-1 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin" />
      </div>
      <BottomNav />
    </div>
  );

  const isActive = sub && sub.is_active;

  return (
    <div className="min-h-screen bg-dark flex flex-col">
      <AppHeader />

      <main className="flex-1 px-4 pt-8 pb-24 overflow-y-auto max-w-lg mx-auto w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-amber-600">Reels Creator</h1>
          <p className="text-gray-400 mt-2">Share your expertise with thousands of patients</p>
        </div>

        {message && (
          <div className={`p-4 rounded-xl mb-6 text-sm font-medium border ${messageType === 'success' ? 'bg-green-500/10 text-green-400 border-green-500/20' : 'bg-yellow-400/10 text-yellow-400 border-yellow-400/20'}`}>
            {message}
          </div>
        )}

        {!isActive ? (
          <div className="bg-gray-900/80 rounded-3xl p-8 border border-white/5 shadow-2xl backdrop-blur-xl text-center relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-yellow-500/5 rounded-full blur-3xl -mr-10 -mt-10"></div>
            
            <div className="mb-8 relative">
              <div className="w-16 h-16 bg-yellow-500/10 rounded-full flex items-center justify-center mx-auto mb-4 text-yellow-500 border border-yellow-500/20">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path></svg>
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Creator Subscription</h2>
              <p className="text-gray-400">Unlock the ability to upload health reels to the public feed.</p>
            </div>

            <div className="mb-8">
              <div className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-amber-600 mb-2">
                {applePrice || (iapFinished ? 'Unavailable' : 'Loading...')}
              </div>
              <p className="text-sm font-medium text-gray-500 tracking-wide uppercase">App Store Subscription</p>
            </div>

            <button 
              onClick={buySubscription} 
              disabled={!applePrice && !iapFinished}
              className={`px-8 py-4 rounded-xl font-bold text-lg w-full transition-all duration-300 shadow-xl ${
                !applePrice && !iapFinished ? 'bg-white/5 text-gray-500 cursor-wait border border-white/10' : 
                !applePrice && iapFinished ? 'bg-yellow-400/10 text-yellow-400 cursor-not-allowed border border-yellow-400/20' :
                'bg-gradient-to-r from-yellow-500 to-amber-600 text-black hover:-translate-y-1 hover:shadow-[0_0_20px_rgba(234,179,8,0.4)]'
              }`}
            >
              {!applePrice && !iapFinished ? 'Loading Price...' : !applePrice && iapFinished ? 'Price Unavailable' : 'Subscribe Now'}
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {sub && (
              <div className="bg-green-500/10 rounded-2xl p-5 border border-green-500/20 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center text-green-400">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                  </div>
                  <div>
                    <p className="text-green-400 font-bold text-sm tracking-wide uppercase">Active Creator</p>
                    <p className="text-xs text-gray-400 mt-0.5">Valid till {new Date(sub.end_date).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-gray-900/80 rounded-3xl p-6 md:p-8 border border-white/5 shadow-2xl backdrop-blur-xl">
              <h2 className="text-2xl font-bold text-white mb-6">Upload New Reel</h2>
              
              <div className="space-y-6">
                <div>
                  <label className="text-sm font-medium text-gray-400 uppercase tracking-wide block mb-3">Select Video File</label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="video/mp4,video/quicktime,video/x-msvideo,video/x-matroska,image/jpeg,image/png,image/webp"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  {!preview ? (
                    <button onClick={() => fileInputRef.current?.click()}
                      className="w-full h-48 bg-white/5 border-2 border-dashed border-white/10 rounded-2xl flex flex-col items-center justify-center text-gray-400 hover:border-yellow-500/50 hover:bg-yellow-500/5 transition-all group cursor-pointer">
                      <div className="w-14 h-14 bg-white/5 rounded-full flex items-center justify-center mb-3 group-hover:bg-yellow-500/10 group-hover:text-yellow-500 transition-colors">
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                      </div>
                      <span className="font-medium text-white mb-1">Tap to select a video</span>
                      <span className="text-xs text-gray-500">MP4, MOV, AVI, MKV supported</span>
                    </button>
                  ) : (
                    <div className="relative group rounded-2xl overflow-hidden bg-black border border-white/10">
                      {preview && file?.type?.startsWith('video/') ? (
                        <video src={preview} className="w-full aspect-[9/16] object-cover" controls />
                      ) : (
                        <img src={preview} alt="Preview" className="w-full aspect-[9/16] object-cover" />
                      )}
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-start justify-end p-4 pointer-events-none"></div>
                      <button onClick={() => { setFile(null); setPreview(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                        className="absolute top-4 right-4 bg-yellow-400 hover:bg-yellow-500 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm transition-colors shadow-lg shadow-black/50">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                      </button>
                      <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                        <p className="text-xs text-gray-300 truncate font-medium">{file?.name}</p>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-400 uppercase tracking-wide block mb-3">Duration (seconds)</label>
                  <input type="number" value={duration} onChange={(e) => setDuration(e.target.value)}
                    placeholder="e.g. 15" min="1" max="30"
                    className="w-full bg-white/5 text-white border border-white/10 rounded-xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-yellow-500/50 transition-all placeholder-gray-600" />
                  <p className="text-xs text-yellow-500/80 mt-2 flex items-center gap-1.5">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                    Videos must be 30 seconds or less
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-400 uppercase tracking-wide block mb-3">Caption</label>
                  <textarea value={caption} onChange={(e) => setCaption(e.target.value)}
                    placeholder="Write a catchy caption..."
                    className="w-full bg-white/5 text-white border border-white/10 rounded-xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-yellow-500/50 transition-all placeholder-gray-600" rows={3} />
                </div>

                <button onClick={uploadReel} disabled={uploading || !file}
                  className={`w-full py-4 rounded-xl font-bold text-lg transition-all duration-300 flex items-center justify-center gap-3 ${
                    uploading || !file 
                      ? 'bg-white/5 text-gray-500 cursor-not-allowed border border-white/10' 
                      : 'bg-gradient-to-r from-yellow-500 to-amber-600 text-black hover:-translate-y-1 hover:shadow-[0_0_20px_rgba(234,179,8,0.4)] shadow-xl'
                  }`}>
                  {uploading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                      Uploading...
                    </>
                  ) : 'Submit for Review'}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      <BottomNav />
    </div>
  );
};

export default ReelSubscriptionPage;
